import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditIcon from '@mui/icons-material/Edit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Avatar, Box, Button, Chip, IconButton, Rating, Typography } from '@mui/material';
import React from 'react';
import { SitterProfileResponse } from '../../../../happypaws-shared/src/Sitter/models/SitterProfileResponse';

interface ProfileHeaderProps {
  sitter: SitterProfileResponse;
  onBook: () => void;
  isOwnProfile?: boolean;
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({ sitter, onBook, isOwnProfile = false }) => {
  return (
    <Box
      sx={{
        backgroundColor: 'white',
        borderRadius: 2,
        p: 3,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 3,
          alignItems: { xs: 'center', sm: 'flex-start' },
        }}
      >
        {/* Left Side - Profile Photo and Info */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            flex: 1,
            alignItems: { xs: 'center', sm: 'flex-start' },
          }}
        >
          {/* Profile Photo with Verification Badge */}
          <Box sx={{ position: 'relative' }}>
            <Avatar src={sitter.profilePhotoUrl ?? sitter.photoUrl ?? undefined}>
              {sitter.firstName?.charAt(0)}
            </Avatar>

            {sitter.verified && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  backgroundColor: '#178B8E',
                  borderRadius: '50%',
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '3px solid white',
                }}
              >
                <VerifiedIcon sx={{ color: 'white', fontSize: 20 }} />
              </Box>
            )}
          </Box>

          {/* Info */}
          <Box
            sx={{
              flex: 1,
              textAlign: { xs: 'center', sm: 'left' },
            }}
          >
            {/* Name */}
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              {sitter.firstName} {sitter.lastName}
            </Typography>

            {/* Rating */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1,
                justifyContent: { xs: 'center', sm: 'flex-start' },
              }}
            >
              <Rating
                value={0}
                precision={0.1}
                readOnly
                sx={{
                  '& .MuiRating-iconFilled': {
                    color: '#FFB400',
                  },
                }}
              />
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                New
              </Typography>
              <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
                • No reviews yet
              </Typography>
            </Box>

            {/* Response Time */}
            <Chip
              icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
              label="Responds within an hour"
              size="small"
              sx={{
                mb: 1,
                backgroundColor: '#E8F5F5',
                color: '#178B8E',
                fontWeight: 500,
                '& .MuiChip-icon': {
                  color: '#178B8E',
                },
              }}
            />

            {/* Location */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: '#5A5A5A',
                justifyContent: { xs: 'center', sm: 'flex-start' },
              }}
            >
              <LocationOnIcon sx={{ fontSize: 20 }} />
              <Typography variant="body2">{sitter.username ?? 'Location not set'}</Typography>
            </Box>
          </Box>
        </Box>

        {/* Right Side - Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            alignItems: { xs: 'center', sm: 'flex-end' },
          }}
        >
          {isOwnProfile && (
            <IconButton
              sx={{
                alignSelf: 'flex-end',
                '&:hover': {
                  backgroundColor: '#F5F5F5',
                },
              }}
            >
              <EditIcon />
            </IconButton>
          )}
          {!isOwnProfile && (
            <Button
              variant="contained"
              size="large"
              onClick={onBook}
              sx={{
                backgroundColor: '#178B8E',
                color: 'white',
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#0F6164',
                },
                minWidth: 200,
              }}
            >
              Book {sitter.firstName}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ProfileHeader;
